
import React, { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import StaffWorkingHoursReport from './StaffWorkingHoursReport';
import NightAuditReportsList from './NightAuditReportsList';
import NightAuditDailyReport from './NightAuditDailyReport';
import ParkingReport from './ParkingReport';
import MaintenanceReport from './MaintenanceReport';
import AttendanceReport from './AttendanceReport';
import DailyOperationReport from './DailyOperationReport';
import PayrollReport from './PayrollReport';
import { database, subscribeToPath } from '../../services/database';
import { StaffMember, Shift, AttendanceRecord, AttendanceRequest, Department, Schedule, AdminUser, REPORT_CONFIG, ReportId, MaintenanceRequest, ParkingReservation, DailyNightAuditReport } from '../../types';

interface ReportsViewProps {
    onInitiateStaffWorkingHoursPrint?: (data: any) => void;
    onInitiatePayrollReport?: (startDate: string, endDate: string, employeeId?: string) => void;
    staffName: string;
    adminUser: AdminUser | null;
    staffList: StaffMember[];
    shifts: Shift[];
    schedule: Schedule;
    attendanceRecords: AttendanceRecord[];
    attendanceRequests: AttendanceRequest[];
    departments: Department[];
    propertyName: string;
    maintenanceRequests: MaintenanceRequest[];
    parkingReservations: ParkingReservation[];
    nightAuditReports: DailyNightAuditReport[];
}

type ReportViewLevel = 'list' | 'config' | 'preview' | 'history';

const ReportsView: React.FC<ReportsViewProps> = ({ 
    onInitiateStaffWorkingHoursPrint, 
    onInitiatePayrollReport,
    staffName, 
    adminUser,
    staffList,
    shifts,
    schedule,
    attendanceRecords,
    attendanceRequests,
    departments,
    propertyName,
    maintenanceRequests,
    parkingReservations,
    nightAuditReports
}) => {
    const [level, setLevel] = useState<ReportViewLevel>('list');
    const [selectedReportId, setSelectedReportId] = useState<ReportId | null>(null);

    const hasPermission = (reportId: ReportId) => {
        if (!adminUser) return false;
        const role = adminUser.role?.toLowerCase();
        if (role === 'super admin' || role === 'admin') return true;
        const permission = adminUser.reportPermissions?.[reportId];
        return permission && permission !== 'none';
    };

    const getPermissionLevel = (reportId: ReportId) => {
        if (!adminUser) return 'none';
        const role = adminUser.role?.toLowerCase();
        if (role === 'super admin' || role === 'admin') return 'export';
        return adminUser.reportPermissions?.[reportId] || 'none';
    };

    const handleSelectReport = (id: ReportId) => {
        setSelectedReportId(id);
        setLevel('config');
    };

    const handleBack = () => {
        if (level === 'preview' || level === 'history') setLevel('config');
        else if (level === 'config') {
            setLevel('list');
            setSelectedReportId(null);
        }
    };


    const renderLevel1 = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REPORT_CONFIG.filter(report => hasPermission(report.id as ReportId)).map((report) => (
                <button
                    key={report.id}
                    onClick={() => handleSelectReport(report.id as ReportId)}
                    className="p-6 bg-white dark:bg-slate-900 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:border-indigo-100 dark:hover:border-indigo-900 transition-all text-left group"
                >
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4 group-hover:scale-110 transition-transform">
                        {report.id === 'workingHours' ? <Icons.Clock className="w-6 h-6" /> : 
                         report.id === 'nightAuditSummary' ? <Icons.Moon className="w-6 h-6" /> :
                         report.id === 'dailyOperation' ? <Icons.BarChart className="w-6 h-6" /> :
                         report.id === 'parking' ? <Icons.Car className="w-6 h-6" /> :
                         report.id === 'maintenance' ? <Icons.Tool className="w-6 h-6" /> :
                         report.id === 'attendance' ? <Icons.UserCheck className="w-6 h-6" /> :
                         report.id === 'payrollSummary' ? <Icons.FileText className="w-6 h-6" /> :
                         <Icons.ClipboardCheck className="w-6 h-6" />}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{report.label}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Generate and manage {report.label.toLowerCase()} for the property.</p>
                </button>
            ))}
            
            {/* Forbidden / Future Reports List Placeholders */}
            <div className="p-6 bg-gray-50/50 dark:bg-slate-900/30 rounded-[32px] border border-dashed border-gray-200 dark:border-slate-800 flex flex-col items-center justify-center text-center opacity-60">
                <Icons.Rocket className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-3" />
                <span className="text-xs font-black uppercase tracking-widest text-gray-400">More Coming Soon</span>
            </div>
        </div>
    );

    const renderLevel2And3 = () => {
        if (!selectedReportId) return null;

        const report = REPORT_CONFIG.find(r => r.id === selectedReportId);
        const permissionLevel = getPermissionLevel(selectedReportId);

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4 no-print mb-8">
                    <button 
                        onClick={handleBack}
                        className="w-10 h-10 rounded-full bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <nav className="flex items-center gap-2 text-sm font-medium">
                        <span className="text-gray-400">Reports</span>
                        <Icons.ChevronRight className="w-4 h-4 text-gray-300" />
                        <span className="text-gray-900 dark:text-white font-bold">{report?.label}</span>
                        {level === 'preview' && (
                            <>
                                <Icons.ChevronRight className="w-4 h-4 text-gray-300" />
                                <span className="text-gray-400">Preview</span>
                            </>
                        )}
                        {level === 'history' && (
                            <>
                                <Icons.ChevronRight className="w-4 h-4 text-gray-300" />
                                <span className="text-gray-400">History</span>
                            </>
                        )}
                    </nav>
                </div>

                {selectedReportId === 'workingHours' && (
                    <StaffWorkingHoursReport 
                        staffList={staffList}
                        shifts={shifts}
                        schedule={schedule}
                        attendanceRecords={attendanceRecords}
                        attendanceRequests={attendanceRequests}
                        departments={departments}
                        propertyName={propertyName}
                        onInitiatePrint={onInitiateStaffWorkingHoursPrint}
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : 'form'}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'nightAuditSummary' && (
                    <NightAuditDailyReport 
                        staffName={staffName} 
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : level === 'history' ? 'history' : 'form'}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'attendance' && (
                    <AttendanceReport 
                        attendanceRecords={attendanceRecords}
                        staffList={staffList}
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : 'form'}
                         onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'maintenance' && (
                    <MaintenanceReport 
                        requests={maintenanceRequests}
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : 'form'}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'parking' && (
                    <ParkingReport 
                        reservations={parkingReservations}
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : 'form'}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'dailyOperation' && (
                    <DailyOperationReport 
                        nightAudit={nightAuditReports}
                        maintenance={maintenanceRequests}
                        attendance={attendanceRecords}
                        permissionLevel={permissionLevel}
                        initialLevel={level === 'preview' ? 'preview' : 'form'}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}

                {selectedReportId === 'payrollSummary' && (
                    <PayrollReport 
                        staffList={staffList}
                        attendanceRecords={attendanceRecords}
                        onInitiatePayrollReport={onInitiatePayrollReport || (() => {})}
                        permissionLevel={permissionLevel}
                        onLevelChange={(l) => setLevel(l === 'form' ? 'config' : l as ReportViewLevel)}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-12">
            <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 no-print ${level !== 'list' ? 'hidden' : ''}`}>
                <div>
                    <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center gap-3 uppercase tracking-tight">
                        <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-200 dark:shadow-none">
                            <Icons.FileText className="w-6 h-6 text-white" />
                        </div>
                        Reports Center
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">Select a report to generate, view history, or export data.</p>
                </div>
            </div>

            {level === 'list' ? renderLevel1() : renderLevel2And3()}
        </div>
    );
};

export default ReportsView;
